import { assortmentHasDoors, getDoors } from 'utils/transformations/doors'
import {
  getBudget,
  getCurrency,
  getPriceChangeAdjustments,
} from 'utils/transformations/financials'
import {
  relayConnectionToArray,
  relayConnectionToArrayDeep,
} from 'utils/transformations/graphql'
import { getItemColors, getItems } from 'utils/transformations/items'
import { getSettings } from 'utils/transformations/settings'
import { naturalSortByCodeAndName } from 'utils/transformations/sorting'
import { getRetailerTags } from 'utils/transformations/tags'

import { fromSkuId } from 'shop/products/utils'

import {
  getBudgetsTotal,
  getCollectionNames,
  getDivisionCodes,
  getDivisionNames,
  getLineItems,
} from './transformations/orders'

const WHOLESALE_CURRENCY = 'wholesaleCurrency'
const RETAIL_CURRENCY = 'retailCurrency'

export const getAssortments = (assortments) =>
  relayConnectionToArray(assortments)

export const getAssortment = (assortment) => {
  const doors = naturalSortByCodeAndName(getDoors(assortment.doors))
  const priceChangeAdjustments = getPriceChangeAdjustments(
    assortment.priceChanges,
  )
  const items = getItems(
    assortment.items,
    priceChangeAdjustments,
    assortment.retailPriceRounding,
  )
  const applicableItemColorTags = relayConnectionToArrayDeep(
    assortment.applicableItemColorTags,
  )
  const tags = getRetailerTags(assortment.retailerAccount.itemColorTags)
  const validationSettings = getSettings(assortment.retailerAccount.settings)
  const itemColors = getItemColors(items, doors, tags, validationSettings)
  const bulkDoorQuantities = itemColors.reduce(
    (bulkQuantities, itemColor) =>
      bulkQuantities.concat(itemColor.bulkDoorQuantities),
    [],
  )
  const wholesaleCurrency = getCurrency(assortment, WHOLESALE_CURRENCY)
  const retailCurrency = getCurrency(assortment, RETAIL_CURRENCY)
  const budget = getBudget(assortment)

  return {
    ...assortment,
    applicableItemColorTags,
    priceChangeAdjustments,
    wholesaleCurrency,
    validationSettings,
    retailCurrency,
    budget,
    doors,
    items,
    itemColors,
    bulkDoorQuantities,
    tags,
    hasDoors: assortmentHasDoors(doors),
  }
}

export const getDataFromSkuId = (skuId) => {
  if (!skuId) {
    return {}
  }
  const { colorVariantId, sizeVariantId } = fromSkuId(skuId)

  return {
    color: colorVariantId,
    size: sizeVariantId,
  }
}

// ====================== orders ==========================
// TODO(vaskinyy) consider moving calculated fields to the backend or to separate queries
// that are called by the actions
export const getOrders = (orders) => {
  const formattedOrders = relayConnectionToArrayDeep(orders) || []
  return formattedOrders.map((formattedOrder) => ({
    ...formattedOrder,
    type: relayConnectionToArrayDeep(formattedOrder.type),
    divisions: relayConnectionToArray(formattedOrder.divisions),
    divisionNames: getDivisionNames(formattedOrder),
    divisionCodes: getDivisionCodes(formattedOrder),
    collectionNames: getCollectionNames(formattedOrder),
    lineItems: getLineItems(formattedOrder.lineItems),
    budgetsTotal: getBudgetsTotal(formattedOrder.budgets),
    latestExports: relayConnectionToArray(formattedOrder.latestExports),
  }))
}

export const addDivisionsToOrders = (ordersDivisions, ordersWithoutDivisions) =>
  ordersWithoutDivisions.map((order) => {
    const matchingOrderDivisions = ordersDivisions.find(
      ({ id }) => id === order.id,
    )
    return matchingOrderDivisions
      ? {
          ...order,
          divisions: matchingOrderDivisions.divisions,
          divisionNames: matchingOrderDivisions.divisionNames,
          divisionCodes: matchingOrderDivisions.divisionCodes,
        }
      : order
  })

// ====================== accounts ==========================
export const getOrderExportIntegrations = (viewerBrand) => {
  const connection = viewerBrand ? viewerBrand.orderExportIntegrations : {}
  const orderExportIntegrations = relayConnectionToArrayDeep(connection)
  return orderExportIntegrations.map((orderExportIntegration) => ({
    id: orderExportIntegration.id,
    name: orderExportIntegration.application.name,
    isManuallyScheduled: orderExportIntegration.isManuallyScheduled,
    integrationType: orderExportIntegration.application.integrationType,
  }))
}
